import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChildOccupancyInput,
  GuestOccupancyInput,
  ResourceOccupancyInput,
  useDeleteReservedResourceMutation,
  useReservedResourcesCheckInMutation,
  useUndoCheckinReservedResourcesMutation,
  useReservedResourcesCheckOutMutation,
  useUndoCheckoutReservedResourcesMutation,
  CheckinCheckoutReservedResourcesInput,
  ReservedResourcePhase,
} from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useUpdateReservedResourceHook from 'src/app/hooks/update-reserved-resource/update-reserved-resource.hook'
import { UpdateReservedResourceOutput } from 'src/app/hooks/update-reserved-resource/update-reserved-resource.types'
import { compareDates } from 'src/utils/date'
import { ReservedResourceF } from '../../../../types/reservation-detail.types'
import { EditRRSchema } from './edit-rr.schema'
import { GuestCheckinCheckoutAction } from '../../../../types/guests.types'

export default function useEditRRMutation(reservedResource: ReservedResourceF) {
  const { id: tenantId } = useTenant()
  const { updateReservedResourceMutation } = useUpdateReservedResourceHook()
  const [deleteReservedResourceMutation] = useDeleteReservedResourceMutation({ refetchQueries: ['ReservationDetail', 'ReservationSearch'] })

  const [doCheckinMutation] = useReservedResourcesCheckInMutation({ refetchQueries: ['ReservationDetail'] })
  const [undoCheckinMutation] = useUndoCheckinReservedResourcesMutation({ refetchQueries: ['ReservationDetail'] })
  const [doCheckoutMutation] = useReservedResourcesCheckOutMutation({ refetchQueries: ['ReservationDetail'] })
  const [undoCheckoutMutation] = useUndoCheckoutReservedResourcesMutation({ refetchQueries: ['ReservationDetail'] })

  const { t } = useTranslation()
  const editRR = useCallback(
    async (data: EditRRSchema, dailyPrices: { day: Date; price: number }[], isTimeResource?: boolean, timeResourceDailyManualPrice?: number): Promise<UpdateReservedResourceOutput> => {
      const start = data.start ?? reservedResource.start
      const end = data.end ?? reservedResource.end

      const resourceOccupancies = data.resourceOccupancies.map<ResourceOccupancyInput>((ro) => ({
        start: ro.start,
        end: ro.end,
        resourceTypeId: ro.reservedResourceTypeId,
        assignedResourceTypeId: ro.assignedResourceTypeId,
        resourceId: ro.resourceId,
      }))

      const adults = data.guests.adults ?? 0
      const childOccupancies: ChildOccupancyInput[] = data.guests.children.reduce((p: ChildOccupancyInput[], v) => {
        if (!v.quantity) return p
        return [...p, { childId: v.childId, quantity: v.quantity }]
      }, [])
      const children = childOccupancies.reduce((p, v) => p + v.quantity, 0)

      if (adults + children < (reservedResource.guestOccupancies?.filter((g) => g.phase !== ReservedResourcePhase.CHECKOUT) ?? []).length) {
        return { result: 'ERROR', error: t('pms.booking.reservation.reserved_resource_edit.error.guest_occupancies_saved') }
      }

      const guestOccupancies: GuestOccupancyInput[] = (reservedResource.guestOccupancies ?? []).map((go) => {
        const goStart =
          compareDates(go.start, reservedResource.start) == 0 //
            ? start
            : compareDates(start, go.start) < 0
            ? go.start
            : start

        const goEnd =
          compareDates(go.end, reservedResource.end) == 0 //
            ? end
            : compareDates(go.end, end) < 0
            ? go.end
            : end

        return {
          type: go.type,
          cityTaxExoneration: go.cityTaxExoneration,
          guestId: go.guest.id,
          start: goStart,
          end: goEnd,
        }
      })

      const rateId = data.rateId ?? reservedResource.rate.id

      let manualPrice: number | null = null
      let manualDailyPricesUpd: { day: Date; price: number }[] | null = null
      dailyPrices = dailyPrices.map((dailyPrice) => ({ ...dailyPrice, price: Math.round(dailyPrice.price) }))
      const firstDayPrice = dailyPrices[0]?.price
      const samePriceForEveryDay = dailyPrices.every(({ price }) => price === firstDayPrice)

      if (!isTimeResource) {
        if (reservedResource.manualPrice || (reservedResource.manualDailyPrices && reservedResource.manualDailyPrices.length)) {
          if (samePriceForEveryDay && !isTimeResource) {
            manualPrice = firstDayPrice
            manualDailyPricesUpd = null
          } else {
            manualPrice = null
            manualDailyPricesUpd = dailyPrices
          }
        } else if (reservedResource.dailyPricesStored && reservedResource.dailyPricesStored.dailyPrices.length) {
          const areEquals = reservedResource.dailyPricesStored.dailyPrices.every((stored) => {
            return dailyPrices.find(({ day }) => compareDates(day, stored.day) === 0)?.price === stored.price
          })
          if (!areEquals) {
            manualPrice = null
            manualDailyPricesUpd = dailyPrices
          }
        }
      } else {
        if (timeResourceDailyManualPrice) manualPrice = Math.round(timeResourceDailyManualPrice)
      }

      const result = await updateReservedResourceMutation({
        reservedResourceId: reservedResource.id,
        input: {
          tenantId: tenantId,
          reservationId: reservedResource.reservationId,
          chargeCategoryId: reservedResource.chargeCategory.id,
          resourceOccupancies,
          adults: adults,
          childOccupancies,
          children,
          guestOccupancies,
          rateId,
          manualPrice: manualPrice,
          manualDailyPrices: manualDailyPricesUpd,
          time: data.time,
        },
      })
      return result
    },
    [reservedResource],
  )

  const deleteRR = useCallback(async () => {
    return await deleteReservedResourceMutation({
      variables: {
        tenantId: tenantId,
        deleteReservedResourceId: reservedResource.id,
      },
    })
  }, [reservedResource])

  const performRRCheckinCheckout = useCallback(
    async (reservedResourceId: string, action: GuestCheckinCheckoutAction, onSuccessCallback: (phase: ReservedResourcePhase) => void) => {
      const input: CheckinCheckoutReservedResourcesInput = {
        tenantId: tenantId,
        reservedResources: [{ reservedResourceId: reservedResourceId, guestIds: [] }],
      }
      let resultPhase: ReservedResourcePhase | undefined = undefined
      try {
        let result = undefined
        switch (action) {
          case 'DO_CHECKIN':
            result = await doCheckinMutation({ variables: { input } })
            resultPhase = ReservedResourcePhase.CHECKIN
            break
          case 'UNDO_CHECKIN':
            result = await undoCheckinMutation({ variables: { input } })
            resultPhase = ReservedResourcePhase.RESERVATION
            break
          case 'DO_CHECKOUT':
            result = await doCheckoutMutation({ variables: { input } })
            resultPhase = ReservedResourcePhase.CHECKOUT
            break
          case 'UNDO_CHECKOUT':
            result = await undoCheckoutMutation({ variables: { input } })
            resultPhase = ReservedResourcePhase.CHECKIN
            break
        }
        if (result && !result.errors && resultPhase) {
          onSuccessCallback(resultPhase)
        }
      } catch (errors) {
        console.log('FIXME: ', errors)
      }
    },
    [tenantId, doCheckinMutation, undoCheckinMutation, doCheckoutMutation, undoCheckoutMutation],
  )

  return { editRR, deleteRR, performRRCheckinCheckout }
}
